import { classNames } from '@panwds/tailwindcss';
import { format, isAfter, isBefore, isEqual, isValid, startOfDay } from 'date-fns';
import React, { cloneElement, useCallback, useEffect, useState, version } from 'react';
import { useIntl } from 'react-intl';
import { getDataTestId } from '../../../utils/index.js';
import { noop } from '../../../utils/noop.js';
import { Button } from '../../Button/Button.js';
import { TruncateText } from '../../TruncateText/TruncateText.js';
import { Body } from '../../Typography/Body.js';
import { Calendar } from '../Calendar/index.js';
import { DateInput } from '../date_input/date_input.js';
import { useActiveDate, useDateRangeState } from '../hooks/index.js';
import { useDateFormat } from '../hooks/useDateFormat.js';
import { useMinMaxDate } from '../hooks/useMinMaxDate.js';
import { SelectPickerLayout } from '../layout/select_picker_layout.js';
import { MAX_DATE, MIN_DATE } from '../utilities/constants.js';
import { applyMessage, cancelMessage, getInitialActiveDate, getTodayDate, validateSelectedDateRange } from '../utilities/index.js';
export const SelectDateRangePicker = ({ button, className, dataMetrics, dataTestId: propsDataTestId, dateFormat = 'MM/dd/yyyy', disabled = false, initialActiveDate = startOfDay(getTodayDate()), initialSelectedValues, items, label, max: propsMax = MAX_DATE, min: propsMin = MIN_DATE, onApply = noop, placeholder, onCancel = noop, weekStartsOn = 0, getCustomLabel }) => {
    const intl = useIntl();
    const { pattern } = useDateFormat(dateFormat);
    const { min, max } = useMinMaxDate({ min: propsMin, max: propsMax, pattern });
    const dataTestId = getDataTestId('select_date_range_picker', propsDataTestId);
    const [activeDate, setActiveDate] = useActiveDate({
        initialActiveDate: getInitialActiveDate({ initialActiveDate, initialDate: initialSelectedValues?.start }),
        max,
        min
    });
    const [isOpen, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState();
    const [previousDateRange, setPreviousDateRange] = useState();
    const [previousSelectedItem, setPreviousSelectedItem] = useState();
    const { selectDate, setEndDate, setStartDate, dateRange } = useDateRangeState(undefined, min, max);
    const [hoverDate, setHoverDate] = useState();
    useEffect(() => {
        if (!initialSelectedValues) {
            setSelectedItem(undefined);
            setPreviousSelectedItem(undefined);
            setPreviousDateRange(undefined);
            setStartDate(undefined);
            setEndDate(undefined);
            return;
        }
        if (initialSelectedValues) {
            const selectedOption = items.find((item) => item.value === initialSelectedValues.value);
            if (selectedOption &&
                selectedOption.value === 'custom' &&
                validateSelectedDateRange({
                    start: initialSelectedValues.start,
                    end: initialSelectedValues.end,
                    min,
                    max
                }) &&
                (previousDateRange?.end && previousDateRange?.start
                    ? format(initialSelectedValues.end, pattern) !== format(previousDateRange.end, pattern) ||
                        format(initialSelectedValues.start, pattern) !== format(previousDateRange.start, pattern)
                    : true)) {
                setStartDate(initialSelectedValues.start);
                setEndDate(initialSelectedValues.end);
                setActiveDate(startOfDay(initialSelectedValues.start));
                setPreviousDateRange({ start: initialSelectedValues.start, end: initialSelectedValues.end });
                setSelectedItem(selectedOption);
                setPreviousSelectedItem(selectedOption);
                return;
            }
            if (selectedOption &&
                selectedOption.value === 'custom' &&
                !initialSelectedValues?.start &&
                !initialSelectedValues?.end) {
                setSelectedItem(selectedOption);
                setPreviousSelectedItem(selectedOption);
                setPreviousDateRange(undefined);
                setStartDate(undefined);
                setEndDate(undefined);
            }
            if (selectedOption && selectedOption.value !== 'custom') {
                setSelectedItem(selectedOption);
                setPreviousSelectedItem(selectedOption);
                setPreviousDateRange(undefined);
                setStartDate(undefined);
                setEndDate(undefined);
            }
        }
    }, [initialSelectedValues]);
    function handleSelectDate(next) {
        selectDate(next);
        setActiveDate(next);
        setHoverDate(undefined);
    }
    function handleSelected(selectedOption) {
        setSelectedItem(selectedOption);
        if (selectedOption.value === 'custom') {
            return;
        }
        setPreviousSelectedItem(selectedOption);
        setPreviousDateRange(undefined);
        setStartDate(undefined);
        setEndDate(undefined);
        setOpen(false);
        onApply({ ...selectedOption });
    }
    function handleStart(date) {
        if (!dateRange.end) {
            setStartDate(date);
        }
        else if (isAfter(date, dateRange.end) || isEqual(date, dateRange.end)) {
            setStartDate(date);
            setEndDate(date);
        }
        else {
            setStartDate(date);
        }
    }
    function handleEnd(date) {
        if (!dateRange.start) {
            setEndDate(date);
        }
        else if (isBefore(date, dateRange.start) || isEqual(date, dateRange.start)) {
            setStartDate(date);
            setEndDate(date);
        }
        else {
            setEndDate(date);
        }
    }
    const togglePopover = useCallback((e) => {
        // Between react 16 and 17 event propagation changed such that 17 and after we need to stop propagation of this event,
        // while for 16 we need to let the event propagate.
        if (!/^16/.test(version)) {
            e.stopPropagation();
        }
        setOpen(!isOpen);
    }, [isOpen, setOpen]);
    const customLabel = getCustomLabel?.({
        ...selectedItem,
        intermittentStart: dateRange?.start,
        intermittentEnd: dateRange?.end,
        start: previousDateRange?.start,
        end: previousDateRange?.end
    });
    const DefaultButton = (React.createElement(Button, { appearance: "tertiary", addClassName: classNames(!disabled
            ? 'dark:twds-text-white hover:twds-cursor-pointer'
            : 'twds-text-disabled dark:twds-text-disabled hover:twds-cursor-not-allowed', typeof className === 'object' ? className.selectButton : undefined) }, React.isValidElement(customLabel) ? (customLabel) : (React.createElement(TruncateText, null, customLabel ??
        selectedItem?.['children'] ??
        selectedItem?.value ??
        (placeholder?.selectButton && (React.createElement(Body, { appearance: "secondary", addClassName: classNames('twds-text-xs', !disabled && 'dark:group-hover:twds-text-gray-300 dark:group-active:twds-text-gray-200') }, placeholder.selectButton)))))));
    const trigger = cloneElement(button || DefaultButton, {
        onMouseDown: togglePopover,
        dataMetrics: dataMetrics,
        dataTestId: `${dataTestId}-menu-icon`,
        isMenu: true,
        isOpen: isOpen,
        disabled: disabled
    });
    const onCancelHandler = () => {
        setActiveDate(initialActiveDate && isValid(initialActiveDate) ? initialActiveDate : getTodayDate());
        setSelectedItem(previousSelectedItem);
        if (previousSelectedItem?.value === 'custom' && previousDateRange?.start && previousDateRange.end) {
            setStartDate(previousDateRange.start);
            setEndDate(previousDateRange.end);
        }
        else {
            setPreviousDateRange(undefined);
            setStartDate(undefined);
            setEndDate(undefined);
        }
        setHoverDate(undefined);
        onCancel();
        setOpen(false);
    };
    return (React.createElement(SelectPickerLayout, { className: typeof className === 'object' ? className.selectPickerLayout : className, dataMetrics: dataMetrics, dataTestId: dataTestId, open: isOpen, onCancel: onCancelHandler, items: items, button: trigger, actions: React.createElement(React.Fragment, null,
            React.createElement(Button, { addClassName: typeof className === 'object' ? className.cancelButton : undefined, dataTestId: getDataTestId('cancel_button', dataTestId), disabled: disabled, onClick: onCancelHandler }, intl.formatMessage(cancelMessage)),
            React.createElement(Button, { appearance: "primary", addClassName: typeof className === 'object' ? className.confirmButton : undefined, dataTestId: getDataTestId('apply_button', dataTestId), disabled: disabled ||
                    !dateRange.start ||
                    !dateRange.end ||
                    (previousDateRange?.end &&
                        previousDateRange?.start &&
                        isEqual(dateRange.end, previousDateRange?.end) &&
                        isEqual(dateRange.start, previousDateRange?.start)), onClick: () => {
                    setPreviousDateRange({ start: dateRange.start, end: dateRange.end });
                    setPreviousSelectedItem(selectedItem);
                    onApply({
                        ...selectedItem,
                        ...(typeof selectedItem === 'object' && selectedItem.value === 'custom'
                            ? { start: dateRange.start, end: dateRange.end }
                            : {})
                    });
                    setOpen(false);
                } }, intl.formatMessage(applyMessage))), inputs: React.createElement("div", { className: "twds-flex twds-space-x-1" },
            React.createElement(DateInput, { dataTestId: getDataTestId('start', dataTestId), label: label?.dateInput?.start, showLabel: !!label?.dateInput?.start, className: {
                    inputClassName: classNames('twds-box-border twds-w-[132px]', typeof className === 'object' && className.startDateInput?.inputClassName)
                }, formatPattern: pattern, placeholder: placeholder?.dateInput?.start, max: max, min: min, selectedDate: dateRange.start, onSelectDate: handleStart, connectLabel: true }),
            React.createElement(DateInput, { dataMetrics: dataMetrics, dataTestId: getDataTestId('end', dataTestId), label: label?.dateInput?.end, showLabel: !!label?.dateInput?.end, className: {
                    inputClassName: classNames('twds-box-border twds-w-[132px]', typeof className === 'object' && className.startDateInput?.inputClassName)
                }, formatPattern: pattern, placeholder: placeholder?.dateInput?.end, max: max, min: min, selectedDate: dateRange.end, onSelectDate: handleEnd, connectLabel: true })), calendar: React.createElement(Calendar, { activeDate: activeDate, dataTestId: dataTestId, dataMetrics: dataMetrics, hoverDate: hoverDate, max: max, min: min, onActiveDateChange: setActiveDate, onHoverDateChange: setHoverDate, onSelectedDateChange: handleSelectDate, dateRange: dateRange, weekStartsOn: weekStartsOn }), max: max, min: min, selectedItem: selectedItem, onSelectOption: handleSelected }));
};
//# sourceMappingURL=select_date_range_picker.js.map